// Chrome's built-in Google Translate (and similar extensions) rewrite the
// page as it translates: text nodes are replaced by <font> wrappers holding
// the translated text, and inline elements can be moved inside such wrappers
// when a sentence is restructured. React keeps references to the original
// nodes and crashes during commit with
//   NotFoundError: Failed to execute 'removeChild' on 'Node'
//   NotFoundError: Failed to execute 'insertBefore' on 'Node'
//   NotFoundError: Failed to execute 'replaceChild' on 'Node'
// (facebook/react#11538), which unmounts the whole app — merchants saw a
// white screen on /setup when changing the pass type while translating.
//
// Recovery strategy, best effort first:
//  1. Node was moved deeper inside the same parent (wrapped in <font>):
//     re-target the call at the wrapper — the ancestor that is a direct
//     child of the calling parent. Removing/inserting relative to the
//     wrapper produces exactly the DOM the caller intended.
//  2. Node was detached and replaced wholesale (how Translate handles text
//     nodes; the original only lives in Translate's own memory): there is no
//     back-reference to its replacement, so degrade to a no-op (removeChild)
//     or an append (insertBefore) instead of throwing.
// Must be imported before anything renders.

// The ancestor of `node` that is a direct child of `parent` (e.g. the <font>
// wrapper Google Translate inserted between them), or null when `node` is no
// longer inside `parent` at all.
function directChildAncestor(parent, node) {
  let current = node;
  while (current && current.parentNode !== parent) {
    current = current.parentNode;
  }
  return current;
}

let warnedRemove = false;
let warnedInsert = false;
let warnedReplace = false;

if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      const wrapper = directChildAncestor(this, child);
      if (wrapper) {
        // Removing the wrapper takes the translated stand-in out with it —
        // the same visual result the caller asked for.
        originalRemoveChild.call(this, wrapper);
        return child;
      }
      if (!warnedRemove) {
        warnedRemove = true;
        console.warn(
          "removeChild target was detached by a browser extension (e.g. Google Translate) — skipped",
          child,
        );
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      const wrapper = directChildAncestor(this, referenceNode);
      if (wrapper) {
        return originalInsertBefore.call(this, newNode, wrapper);
      }
      if (!warnedInsert) {
        warnedInsert = true;
        console.warn(
          "insertBefore reference node was detached by a browser extension (e.g. Google Translate) — appending instead",
          referenceNode,
        );
      }
      return this.appendChild(newNode);
    }
    return originalInsertBefore.apply(this, arguments);
  };

  const originalReplaceChild = Node.prototype.replaceChild;
  Node.prototype.replaceChild = function (newChild, oldChild) {
    if (oldChild && oldChild.parentNode !== this) {
      const wrapper = directChildAncestor(this, oldChild);
      if (wrapper) {
        // Replacing the wrapper drops the translated stand-in and puts the
        // new node exactly where the caller wanted it.
        return originalReplaceChild.call(this, newChild, wrapper);
      }
      if (!warnedReplace) {
        warnedReplace = true;
        console.warn(
          "replaceChild target was detached by a browser extension (e.g. Google Translate) — appending instead",
          oldChild,
        );
      }
      // No back-reference to where the old node lived, so append the new one
      // rather than throwing; return the old node to match native semantics.
      this.appendChild(newChild);
      return oldChild;
    }
    return originalReplaceChild.apply(this, arguments);
  };
}
